import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { prestamoService } from '../../services/api';
import { useTenantNavigation } from '../../hooks/useTenantNavigation';
import '../../styles/Clientes.css';

const HistorialReenganche = () => {
  const [searchParams] = useSearchParams();
  const prestamoId = searchParams.get('prestamo_id');
  const { tenantNavigate } = useTenantNavigation();
  
  const [historial, setHistorial] = useState([]);
  const [prestamo, setPrestamo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (prestamoId) {
      loadHistorial();
    }
  }, [prestamoId]);

  const loadHistorial = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Cargar información del préstamo
      const prestamoResponse = await prestamoService.getPrestamo(prestamoId);
      setPrestamo(prestamoResponse.data);
      
      // Cargar historial
      try {
        const historialResponse = await prestamoService.getHistorial(prestamoId);
        setHistorial(historialResponse.data.historial || []);
      } catch (historialErr) {
        console.error('Error al cargar historial:', historialErr);
        // Si el error es 404 o no hay historial, mostrar lista vacía
        if (historialErr.response?.status === 404 || historialErr.response?.status === 500) {
          setHistorial([]);
        } else {
          throw historialErr;
        }
      }
      
    } catch (err) {
      console.error('Error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Error al cargar el historial';
      setError(`Error: ${errorMessage}. Verifica la consola para más detalles.`);
    } finally {
      setLoading(false);
    }
  };

  const getEventoIcono = (evento) => {
    const iconos = {
      'creado': '✨',
      'pago': '💰',
      'reenganche': '🔄',
      'refinanciamiento': '🔄',
      'refinanciado': '🔄',
      'reenganchado': '🔄',
      'modificado': '✏️',
      'cerrado': '🔒'
    };
    return iconos[evento] || '📝';
  };

  const getEventoColor = (evento) => {
    const colores = {
      'creado': '#27ae60',
      'pago': '#3498db',
      'reenganche': '#e67e22',
      'refinanciamiento': '#e67e22',
      'refinanciado': '#e67e22',
      'reenganchado': '#e67e22',
      'modificado': '#f39c12',
      'cerrado': '#95a5a6'
    };
    return colores[evento] || '#95a5a6';
  };

  const formatFecha = (fechaString) => {
    if (!fechaString) return '-';
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="clientes-page">
        <div className="loading">Cargando historial...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clientes-page">
        <div className="banner error">{error}</div>
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <button
            className="btn"
            onClick={() => tenantNavigate('/prestamos/lista')}
            style={{ background: '#666' }}
          >
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>🟨 Historial de Reenganche</h1>
        <button
          className="btn"
          onClick={() => tenantNavigate('/prestamos/lista')}
          style={{ background: '#666' }}
        >
          ← Volver
        </button>
      </div>

      {prestamo && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-title" style={{ background: '#3498db' }}>
            Información del Préstamo #{prestamo.id}
          </div>
          <div className="form-grid" style={{ padding: '1rem' }}>
            <div className="form-group">
              <label>Cliente</label>
              <div style={{ padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                {prestamo.cliente?.nombres} {prestamo.cliente?.apellidos}
              </div>
            </div>
            <div className="form-group">
              <label>Monto</label>
              <div style={{ padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                RD$ {prestamo.monto_prestado?.toLocaleString('es-DO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </div>
            </div>
            <div className="form-group">
              <label>Estado</label>
              <div style={{ padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                {prestamo.estado}
              </div>
            </div>
            {prestamo.reemplazado_por && (
              <div className="form-group">
                <label>Reemplazado por</label>
                <div style={{ padding: '0.5rem', background: '#fff3e0', borderRadius: '4px', color: '#e67e22', fontWeight: 'bold' }}>
                  Préstamo #{prestamo.reemplazado_por}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title" style={{ background: '#e67e22' }}>
          Historial de Eventos
        </div>

        {historial.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            <p>No hay eventos registrados en el historial de este préstamo.</p>
          </div>
        ) : (
          <div style={{ padding: '1rem' }}>
            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {historial.map((evento, index) => (
                <div
                  key={evento.id}
                  style={{
                    borderLeft: `4px solid ${getEventoColor(evento.evento)}`,
                    background: '#f9f9f9',
                    padding: '1rem',
                    borderRadius: '4px',
                    position: 'relative'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.5rem' }}>
                      {getEventoIcono(evento.evento)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.25rem'
                      }}>
                        <strong style={{
                          color: getEventoColor(evento.evento),
                          textTransform: 'capitalize',
                          fontSize: '1.1rem'
                        }}>
                          {evento.evento}
                        </strong>
                        {index === 0 && (
                          <span style={{
                            background: '#27ae60',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            INICIO
                          </span>
                        )}
                        {index === historial.length - 1 && (
                          <span style={{
                            background: '#e67e22',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            ÚLTIMO
                          </span>
                        )}
                      </div>
                      <div style={{
                        color: '#666',
                        fontSize: '0.9rem'
                      }}>
                        {formatFecha(evento.fecha)}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    color: '#333',
                    marginTop: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #e0e0e0'
                  }}>
                    {evento.detalle}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorialReenganche;

